"use client"

/**
 * @fileoverview First-visit consent banner for EEA/UK/CH visitors.
 * @description Surfaces an explicit opt-in before analytics storage in
 * consent-required regions. Rest-of-world visitors are unaffected.
 *
 * @module components/privacy/ConsentBanner
 */

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useState, type ReactNode } from "react"
import { Button } from "~/components/Button"
import { shouldShowConsentBanner } from "~/lib/analytics/consent-banner-visibility"
import {
  buildConsentPreferences,
  persistConsentPreferences,
  readStoredConsentPreferences,
  replayEdgeConsentCookiesFromStoredPreferences,
} from "~/lib/analytics/consent-preferences"
import { recordAttributionTouchAsync } from "~/lib/analytics/attribution"
import { CONSENT_CHANGED_EVENT } from "~/lib/analytics/consent"
import { hydrateRegionCookieIfMissing } from "~/lib/analytics/hydrate-region-cookie"
import { pageview, trackPrivacyUiEvent } from "~/lib/gtag"

/**
 * Renders the EEA/UK/CH first-visit consent banner when needed.
 *
 * @returns {ReactNode} Banner UI or null when hidden.
 */
export function ConsentBanner(): ReactNode {
  const pathname = usePathname()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return

    let cancelled = false

    const syncVisibility = (): void => {
      if (cancelled) return
      let nextVisible = false
      try {
        const prefs = readStoredConsentPreferences()
        if (prefs) {
          replayEdgeConsentCookiesFromStoredPreferences()
          setVisible(false)
          return
        }
        nextVisible = shouldShowConsentBanner(pathname)
      } catch {
        return
      }

      setVisible((prev) => {
        if (nextVisible && !prev) {
          trackPrivacyUiEvent("consent_banner_shown", { surface: "eea_first_visit" })
        }
        return nextVisible
      })
    }

    const run = async (): Promise<void> => {
      await hydrateRegionCookieIfMissing()
      if (pathname === "/privacy/choices") {
        replayEdgeConsentCookiesFromStoredPreferences()
        if (!cancelled) setVisible(false)
        return
      }
      syncVisibility()
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [pathname])

  useEffect(() => {
    if (typeof window === "undefined") return
    const sync = (): void => {
      if (!shouldShowConsentBanner(pathname)) {
        setVisible(false)
      }
    }
    window.addEventListener(CONSENT_CHANGED_EVENT, sync)
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, sync)
  }, [pathname])

  const hide = (): void => setVisible(false)

  const acceptAll = (): void => {
    persistConsentPreferences(buildConsentPreferences(true))
    void recordAttributionTouchAsync(window.location.pathname)
    const query = window.location.search || ""
    pageview(`${window.location.pathname}${query}`)
    trackPrivacyUiEvent("consent_banner_interaction", { action: "accept_all" })
    hide()
  }

  const rejectNonEssential = (): void => {
    persistConsentPreferences(buildConsentPreferences(false))
    trackPrivacyUiEvent("consent_banner_interaction", { action: "reject_non_essential" })
    hide()
  }

  if (!visible) return null

  return (
    <div
      role="region"
      aria-labelledby="consent-banner-title"
      aria-describedby="consent-banner-desc"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-gray-200/80 bg-white/95 p-4 shadow-2xl backdrop-blur-md dark:border-white/10 dark:bg-gray-950/95 sm:bottom-4 sm:inset-x-4 sm:rounded-2xl sm:border"
      style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))" }}
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div className="min-w-0 flex-1 space-y-1 text-sm text-gray-700 dark:text-gray-300">
          <p id="consent-banner-title" className="font-medium text-gray-900 dark:text-white">
            We use cookies for analytics
          </p>
          <p id="consent-banner-desc">
            We use privacy-friendly analytics to improve adsights.ai. Accepting lets us count your
            visit in our reports; rejecting keeps only essential cookies. You can change this anytime
            in{" "}
            <Link
              href="/privacy/choices"
              className="font-medium text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
              onClick={() => {
                trackPrivacyUiEvent("consent_banner_interaction", { action: "manage" })
              }}
            >
              Privacy choices
            </Link>
            .
          </p>
        </div>
        <div
          className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center"
          role="group"
          aria-label="Cookie consent actions"
        >
          <Button
            type="button"
            variant="secondary"
            className="min-h-10 flex-1 whitespace-nowrap sm:flex-none"
            onClick={rejectNonEssential}
          >
            Reject non-essential
          </Button>
          <Button
            type="button"
            variant="primary"
            className="min-h-10 flex-1 whitespace-nowrap sm:flex-none"
            onClick={acceptAll}
          >
            Accept analytics
          </Button>
        </div>
      </div>
    </div>
  )
}
